/**
 * 방사선(ARS) 중증도분류 — 외상·복합 2열, 확대뷰 ARS 행.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { TriageResultShell } from './TriageResultShell';
import type { WinnerEntry } from './Top3Winners';
import { isTriageResponseCorrect } from './simulationResultBlocks';
import {
    type TriagePatientRow,
    type TriageViewMode,
    getTriageStageAnswers,
    saltTriageCapsuleStyle,
} from './triageShared';

export interface RadiationTriageResultTableProps {
    title: string;
    patients: TriagePatientRow[];
    viewMode?: TriageViewMode;
    onViewModeChange?: (mode: TriageViewMode) => void;
    isAllParticipantsView?: boolean;
    winners?: WinnerEntry[];
}

const RadiationEnlargedView: React.FC<{
    row: TriagePatientRow;
    isAllParticipantsView?: boolean;
}> = ({ row, isAllParticipantsView }) => {
    const { t } = useTranslation('class');
    const { t: tRadiation } = useTranslation('radiation');
    const { firstCorrect, firstUser, secondCorrect, secondUser } = getTriageStageAnswers(row);
    const hasFirstUser = firstUser != null && String(firstUser).trim() !== '';
    const hasSecondCorrect = secondCorrect != null && String(secondCorrect).trim() !== '';
    const hasSecondUser = secondUser != null && String(secondUser).trim() !== '';
    const walkAbility = row.walkAbility ?? '—';
    const responsiveness = row.responsiveness ?? '—';
    const respiration = row.respiration ?? '—';
    const pulse = row.pulse ?? '—';
    const majorInjuries = row.majorInjuries ?? [];
    const chiefComplaint = row.chiefComplaint ?? '—';
    const intervention = row.intervention ?? '—';
    
    // 환자 이름 번역 (TriageName.X → Radiation.Evaluation.PatientName.X)
    const patientName = (() => {
        const name = row.patientName ?? '';
        const match = name.match(/^TriageName\.(\d+)$/);
        if (match) {
            const id = match[1];
            return tRadiation(`Radiation.Evaluation.PatientName.${id}`, { defaultValue: name });
        }
        return name;
    })();
    
    // comments는 i18n 키들이 ' | '로 구분되어 있을 수 있음
    const commentsRaw = row.comments ?? '';
    const commentKeys = commentsRaw.split(' | ').filter(key => key.trim());
    const translatedComments = commentKeys.map(key => tRadiation(key, { defaultValue: key }));
    
    const byColor = row.correctRateByColor;
    const colorOrder = ['red', 'yellow', 'green', 'black'] as const;
    const colorLabel: Record<string, string> = { red: 'Red', yellow: 'Yellow', green: 'Green', black: 'Black' };
    const capsule = (level: string | number) => saltTriageCapsuleStyle(String(level));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="space-y-4">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <tbody className="divide-y divide-gray-200">
                        <tr><td className="px-3 py-2 bg-gray-50 font-medium text-gray-600 w-28">보행여부</td><td className="px-3 py-2">{walkAbility}</td></tr>
                        <tr><td className="px-3 py-2 bg-gray-50 font-medium text-gray-600">반응여부</td><td className="px-3 py-2">{responsiveness}</td></tr>
                        <tr><td className="px-3 py-2 bg-gray-50 font-medium text-gray-600">호흡수</td><td className="px-3 py-2">{respiration}</td></tr>
                        <tr><td className="px-3 py-2 bg-gray-50 font-medium text-gray-600">맥박수</td><td className="px-3 py-2">{pulse}</td></tr>
                        <tr>
                            <td className="px-3 py-2 bg-gray-50 font-medium text-gray-600 align-top">주요손상</td>
                            <td className="px-3 py-2">
                                {majorInjuries.length > 0 ? majorInjuries.map((s, i) => (
                                    <div key={i} className="py-0.5">
                                        <span className="text-red-600 font-bold">{i + 1}-</span> <span>{s}</span>
                                    </div>
                                )) : '—'}
                            </td>
                        </tr>
                        <tr><td className="px-3 py-2 bg-gray-50 font-medium text-gray-600">주호소</td><td className="px-3 py-2">{chiefComplaint}</td></tr>
                        <tr><td className="px-3 py-2 bg-gray-50 font-medium text-gray-600">중재</td><td className="px-3 py-2">{intervention}</td></tr>
                    </tbody>
                </table>
            </div>
            <div className="flex flex-col gap-6">
                <div className="text-xl font-bold text-gray-900">
                    {patientName} <span className="text-gray-600">({row.gender ? '남' : '여'}, {row.age}세)</span>
                </div>
                {isAllParticipantsView && byColor ? (
                    <div className="space-y-3">
                        <div className="text-base font-medium text-gray-500 mb-2">정답율 (%)</div>
                        {colorOrder.map((color) => {
                            const value = Math.min(100, Math.max(0, byColor[color] ?? 0));
                            const isCorrectAnswer = color === row.correctAnswer;
                            return (
                                <div key={color} className="flex items-center gap-4">
                                    <span className={`text-sm font-semibold w-20 text-center rounded px-4 py-2 ${saltTriageCapsuleStyle(color)}`}>
                                        {colorLabel[color]}
                                    </span>
                                    <div className="flex-1 min-w-0 h-3 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${saltTriageCapsuleStyle(color)}`}
                                            style={{ width: `${value}%` }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0 w-28 justify-start">
                                        <span className="text-sm font-medium text-gray-900">{value}%</span>
                                        {isCorrectAnswer && (
                                            <span className="flex items-center justify-center gap-1">
                                                <CheckCircle2 size={28} className="text-brand-500 rounded-full" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="w-full">
                        <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-3 items-stretch">
                            <div className="min-w-[2.75rem]" aria-hidden />
                            <div className="text-sm font-semibold text-gray-700 text-center">외상 분류</div>
                            <div className="text-sm font-semibold text-gray-700 text-center">복합 분류</div>

                            <div className="text-xs font-medium text-gray-500 self-center">정답</div>
                            <div className={`min-h-[42px] rounded-lg flex items-center justify-center font-bold min-w-0 ${capsule(firstCorrect)}`}>
                                {firstCorrect}
                            </div>
                            {hasSecondCorrect ? (
                                <div className={`min-h-[42px] rounded-lg flex items-center justify-center font-bold min-w-0 ${capsule(secondCorrect)}`}>
                                    {secondCorrect}
                                </div>
                            ) : (
                                <div className="min-h-[42px] rounded-lg flex items-center justify-center border border-gray-200 bg-gray-50 text-gray-500 font-semibold min-w-0">
                                    -
                                </div>
                            )}

                            <div className="text-xs font-medium text-gray-500 self-center">답변</div>
                            {hasFirstUser ? (
                                <div className={`min-h-[42px] rounded-lg flex items-center justify-center font-bold min-w-0 ${capsule(firstUser)}`}>
                                    {firstUser}
                                </div>
                            ) : (
                                <div className={`min-h-[42px] rounded-lg flex items-center justify-center font-bold min-w-0 ${capsule('미분류')}`}>
                                    미분류
                                </div>
                            )}
                            {hasSecondUser ? (
                                <div className={`min-h-[42px] rounded-lg flex items-center justify-center font-bold min-w-0 ${capsule(secondUser)}`}>
                                    {secondUser}
                                </div>
                            ) : (
                                <div className="min-h-[42px] rounded-lg flex items-center justify-center border border-gray-200 bg-gray-50 text-gray-500 font-semibold min-w-0">
                                    -
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {translatedComments.length > 0 && (
                    <div className="mt-2 pt-4 border-t border-gray-100">
                        <div className="text-base font-semibold text-gray-900 mb-1">Comments</div>
                        <div className="text-sm text-gray-700 leading-relaxed">
                            {translatedComments.map((comment, idx) => (
                                <div key={idx}>
                                    <span dangerouslySetInnerHTML={{ __html: comment }} />
                                    {idx < translatedComments.length - 1 && <br />}
                                    {idx < translatedComments.length - 1 && <br />}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const RadiationPatientCard: React.FC<{
    row: TriagePatientRow;
    idx: number;
    compact?: boolean;
    isAllParticipantsView?: boolean;
}> = ({ row, compact, isAllParticipantsView }) => {
    const { t: tRadiation } = useTranslation('radiation');
    const { firstCorrect, firstUser, secondCorrect, secondUser } = getTriageStageAnswers(row);
    const hasFirstUser = firstUser != null && String(firstUser).trim() !== '';
    const hasSecondUser = secondUser != null && String(secondUser).trim() !== '';
    const isCorrect = isTriageResponseCorrect(firstCorrect, firstUser);
    const showCorrectRate = isAllParticipantsView && row.correctRate != null;
    const rate = Math.min(100, Math.max(0, row.correctRate ?? 0));
    const capsule = (level: string | number) => saltTriageCapsuleStyle(String(level));

    // 환자 이름 번역 (TriageName.X → Radiation.Evaluation.PatientName.X)
    const patientName = (() => {
        const name = row.patientName ?? '';
        const match = name.match(/^TriageName\.(\d+)$/);
        if (match) {
            const id = match[1];
            return tRadiation(`Radiation.Evaluation.PatientName.${id}`, { defaultValue: name });
        }
        return name;
    })();

    return (
        <div className={`bg-white rounded-xl border border-gray-200/90 shadow-sm relative ${compact ? 'p-3' : 'p-4'}`}>
            <div className={compact ? 'absolute top-2 right-2' : 'absolute top-3 right-3'}>
                {isCorrect ? (
                    <CheckCircle2 size={compact ? 18 : 24} className="text-green-600 shrink-0" />
                ) : (
                    <XCircle size={compact ? 18 : 24} className="text-red-600 shrink-1/2" />
                )}
            </div>
            <div className={`font-semibold text-gray-900 mb-2 ${compact ? 'text-sm pr-14' : 'text-base pr-20'}`}>
                {patientName} <span className="text-gray-600">({row.gender ? '남' : '여'}, {row.age}세)</span>
            </div>
            <div className={compact ? 'space-y-1 text-sm' : 'space-y-2 text-sm'}>
                {!isAllParticipantsView && (
                    <div
                        className={`grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] min-w-0 ${compact ? 'gap-x-2 gap-y-1.5 text-xs' : 'gap-x-3 gap-y-2 text-sm'} items-center`}
                    >
                        <div className={compact ? 'min-w-[1.75rem]' : 'min-w-9'} aria-hidden />
                        <div className={`font-semibold text-gray-700 text-center ${compact ? 'text-xs' : 'text-sm'}`}>외상 분류</div>
                        <div className={`font-semibold text-gray-700 text-center ${compact ? 'text-xs' : 'text-sm'}`}>복합 분류</div>

                        <div className={`text-gray-500 shrink-0 ${compact ? 'text-xs' : 'text-sm'}`}>정답</div>
                        <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule(firstCorrect)} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                            {firstCorrect}
                        </span>
                        {secondCorrect ? (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule(secondCorrect)} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                {secondCorrect}
                            </span>
                        ) : (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full bg-gray-100 text-gray-500 ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                -
                            </span>
                        )}

                        <div className={`text-gray-500 shrink-0 ${compact ? 'text-xs' : 'text-sm'}`}>답변</div>
                        {hasFirstUser ? (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule(firstUser)} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                {firstUser}
                            </span>
                        ) : (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule('미분류')} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                미분류
                            </span>
                        )}
                        {hasSecondUser ? (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule(secondUser)} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                {secondUser}
                            </span>
                        ) : (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full bg-gray-100 text-gray-500 ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                -
                            </span>
                        )}
                    </div>
                )}
                {showCorrectRate && (
                    <div className="flex items-center gap-2 pt-1">
                        <span className={`text-gray-500 shrink-0 ${compact ? 'w-14 text-xs' : 'w-20'}`}>정답율</span>
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${capsule(row.correctAnswer)}`}
                                    style={{ width: `${rate}%` }}
                                />
                            </div>
                            <span className="text-sm font-medium text-gray-900 shrink-0 w-8">{rate}%</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const RadiationTriageResultTable: React.FC<RadiationTriageResultTableProps> = (props) => {
    const { title, patients, viewMode, onViewModeChange, isAllParticipantsView, winners } = props;
    const { t: tRadiation } = useTranslation('radiation');
    
    // 환자 이름 번역 함수
    const formatPatientName = (patientName: string): string => {
        const match = patientName.match(/^TriageName\.(\d+)$/);
        if (match) {
            const id = match[1];
            return tRadiation(`Radiation.Evaluation.PatientName.${id}`, { defaultValue: patientName });
        }
        return patientName;
    };
    
    return (
        <TriageResultShell
            title={title}
            patients={patients}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            isAllParticipantsView={isAllParticipantsView}
            winners={winners}
            noPatientLayout="radiation"
            renderCard={(row, idx) => (
                <RadiationPatientCard key={idx} row={row} idx={idx} compact={false} isAllParticipantsView={isAllParticipantsView} />
            )}
            renderEnlarged={(row) => <RadiationEnlargedView row={row} isAllParticipantsView={isAllParticipantsView} />}
            formatPatientName={formatPatientName}
        />
    );
};
